import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  psd2Provider, psd2Institutions, psd2Connect, psd2Finalize,
  psd2Accounts, psd2SyncAccount, psd2Transactions, psd2ConfirmMatch,
} from '../services/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Landmark, Plus, RefreshCw, Loader2, CheckCircle2, AlertTriangle,
  Link as LinkIcon, Search, Sparkles, HelpCircle, Zap, ShieldCheck
} from 'lucide-react';
import HelpInfoDialog from '../components/help/HelpInfoDialog';

const fmt = (n, c = 'EUR') => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: c }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('es-ES'); } catch { return s; } };

const CONF_LABEL = { high: { l: 'Alta', cls: 'bg-emerald-100 text-emerald-700' }, medium: { l: 'Media', cls: 'bg-amber-100 text-amber-700' }, low: { l: 'Baja', cls: 'bg-rose-100 text-rose-700' }, manual: { l: 'Manual', cls: 'bg-blue-100 text-blue-700' } };

const BankingPsd2Page = () => {
  const [params, setParams] = useSearchParams();
  const [providerInfo, setProviderInfo] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState({});
  const [filter, setFilter] = useState('all');
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, a, t] = await Promise.all([
        psd2Provider(),
        psd2Accounts(),
        psd2Transactions({ limit: 200 }),
      ]);
      setProviderInfo(p.data);
      setAccounts(a.data?.items || []);
      setTxns(t.data?.items || []);
    } catch (e) {
      toast.error('Error cargando banca');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Handle return from bank
    const rid = params.get('requisition_id');
    if (rid) {
      psd2Finalize(rid).then((r) => {
        toast.success(`Conexión completada · ${r.data.accounts_saved} cuenta(s)`);
        params.delete('requisition_id');
        params.delete('mock');
        setParams(params, { replace: true });
        load();
      }).catch(() => toast.error('No se pudo finalizar la conexión'));
    }
  }, [load, params, setParams]);

  const openConnect = async () => {
    setShowConnect(true);
    try {
      const r = await psd2Institutions('ES');
      setInstitutions(r.data?.items || []);
    } catch (e) {
      toast.error('No se pudieron cargar los bancos');
    }
  };

  const connect = async (instId) => {
    try {
      const redirect = `${window.location.origin}/banking-psd2`;
      const r = await psd2Connect(instId, redirect);
      window.location.href = r.data.link;
    } catch (e) {
      toast.error('Error iniciando conexión');
    }
  };

  const sync = async (accId) => {
    setSyncing((s) => ({ ...s, [accId]: true }));
    try {
      const r = await psd2SyncAccount(accId);
      toast.success(`${r.data.inserted} movimientos · ${r.data.matched_auto} conciliados`);
      load();
    } catch (e) {
      toast.error('Error sincronizando');
    } finally {
      setSyncing((s) => ({ ...s, [accId]: false }));
    }
  };

  const confirmMatch = async (txnId, invoiceId) => {
    try {
      await psd2ConfirmMatch(txnId, { invoice_id: invoiceId });
      toast.success('Match confirmado · Factura marcada como pagada');
      load();
    } catch (e) {
      toast.error('Error confirmando');
    }
  };

  const filtered = txns.filter((t) => filter === 'all' ? true : t.status === filter);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;

  return (
    <div className="space-y-6" data-testid="banking-psd2-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-stone-900 flex items-center gap-3">
            <Landmark className="w-8 h-8 text-[#FF6B4A]" />
            Banca PSD2
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Conecta tus cuentas via Open Banking
            {providerInfo?.is_mock && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded uppercase">Modo demo</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowHelp(true)} className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="psd2-help-btn">
            <HelpCircle className="w-4 h-4 mr-1.5" />¿Qué es esto?
          </Button>
          <Button onClick={openConnect} className="bg-[#FF6B4A] text-white" data-testid="connect-bank-btn">
            <Plus className="w-4 h-4 mr-2" /> Conectar banco
          </Button>
        </div>
      </div>

      {providerInfo?.is_mock && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex gap-2">
          <Sparkles className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Estás en modo DEMO con datos simulados</p>
            <p className="text-xs">Para conectar bancos reales, añade tus credenciales <code className="bg-amber-100 px-1 rounded">GOCARDLESS_SECRET_ID</code> y <code className="bg-amber-100 px-1 rounded">GOCARDLESS_SECRET_KEY</code> en el backend y pon <code className="bg-amber-100 px-1 rounded">GOCARDLESS_USE_MOCK=false</code>.</p>
          </div>
        </div>
      )}

      {/* Accounts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.length === 0 ? (
          <div className="md:col-span-3 bg-white rounded-2xl border border-stone-100 p-10 text-center">
            <Landmark className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500">No tienes cuentas conectadas todavía.</p>
            <Button onClick={openConnect} className="mt-3 bg-[#FF6B4A] text-white">Conectar primer banco</Button>
          </div>
        ) : (
          accounts.map((acc) => (
            <div key={acc.id} className="bg-white rounded-2xl border border-stone-100 p-4" data-testid={`bank-account-${acc.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="text-xs text-stone-500 uppercase font-semibold">{acc.provider}</p>
                  <p className="font-bold text-stone-800">{acc.name || 'Cuenta'}</p>
                  <p className="text-xs font-mono text-stone-500 mt-1">{acc.iban}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => sync(acc.id)} disabled={syncing[acc.id]} data-testid={`sync-account-${acc.id}`}>
                  {syncing[acc.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex justify-between items-end">
                <span className="text-xs text-stone-400">Última sync: {acc.last_sync_at ? fmtDate(acc.last_sync_at) : 'Nunca'}</span>
                <span className="text-xl font-bold text-stone-900">{fmt(acc.balance, acc.currency)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Transactions */}
      {accounts.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="p-4 border-b border-stone-100 flex justify-between items-center">
            <h2 className="font-bold text-stone-800">Movimientos</h2>
            <div className="flex gap-1">
              {[{ k: 'all', l: 'Todos' }, { k: 'unreconciled', l: 'Sin conciliar' }, { k: 'matched', l: 'Conciliados' }].map((f) => (
                <button key={f.k} onClick={() => setFilter(f.k)}
                  className={`px-2.5 py-1 text-xs rounded-lg ${filter === f.k ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
                  data-testid={`txn-filter-${f.k}`}>{f.l}</button>
              ))}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs uppercase text-stone-500">
              <tr>
                <th className="text-left px-4 py-2">Fecha</th>
                <th className="text-left px-4 py-2">Descripción</th>
                <th className="text-right px-4 py-2">Importe</th>
                <th className="text-center px-4 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="4" className="text-center py-8 text-stone-400">Sin movimientos en esta vista</td></tr>
              ) : filtered.map((t) => {
                const conf = CONF_LABEL[t.match_confidence];
                return (
                  <tr key={t.id} className="border-t border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-2 text-stone-500">{t.date}</td>
                    <td className="px-4 py-2">
                      <p className="text-stone-800">{t.description}</p>
                      {t.counterparty_name && <p className="text-xs text-stone-400">{t.counterparty_name}</p>}
                    </td>
                    <td className={`px-4 py-2 text-right font-mono font-bold ${t.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmt(t.amount, t.currency)}</td>
                    <td className="px-4 py-2 text-center">
                      {t.status === 'matched' ? (
                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${conf?.cls || 'bg-emerald-100 text-emerald-700'}`}>
                          <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> {conf?.l || 'Conciliado'}
                        </span>
                      ) : (
                        <span className="text-xs text-stone-400">Sin conciliar</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showConnect} onOpenChange={setShowConnect}>
        <DialogContent className="max-w-md" data-testid="connect-bank-dialog">
          <DialogHeader><DialogTitle>Selecciona tu banco</DialogTitle></DialogHeader>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar banco..." className="w-full pl-9 pr-3 py-2 border border-stone-200 rounded-lg text-sm" />
          </div>
          <div className="max-h-[400px] overflow-y-auto space-y-1">
            {institutions.filter((i) => !search || i.name.toLowerCase().includes(search.toLowerCase())).map((inst) => (
              <button key={inst.id} onClick={() => connect(inst.id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-left"
                data-testid={`bank-option-${inst.id}`}>
                <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center text-2xl">
                  {inst.logo || '🏦'}
                </div>
                <div>
                  <p className="font-semibold text-stone-800">{inst.name}</p>
                  {inst.bic && <p className="text-[10px] font-mono text-stone-500">{inst.bic}</p>}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <HelpInfoDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        icon={Landmark}
        title="¿Qué es Banca PSD2?"
        intro={(
          <>
            <p><strong>Conexión legal y segura con tu banco</strong> a través de la directiva europea PSD2/Open Banking.
              Tu banco te deja autorizar a Facturcal para <strong>leer tus movimientos</strong> (no para mover dinero) y
              así <strong>casarlos automáticamente</strong> con tus facturas emitidas.</p>
            <p className="mt-2">Resultado: dejas de marcar facturas como "pagada" a mano. El sistema lo hace solo.</p>
          </>
        )}
        sections={[
          {
            icon: ShieldCheck, iconColor: '#10B981', title: 'Cómo de seguro es',
            body: [
              'Es la <strong>misma tecnología</strong> que usan Bizum, Bnext, Fintonic, Cobee, etc.',
              'Tú autorizas en la <strong>app/web de tu propio banco</strong> (no nos das tus claves)',
              'La autorización <strong>caduca a 90 días</strong> y la renuevas con un clic',
              'Sólo <strong>lectura</strong>: nadie puede mover dinero desde aquí',
            ],
          },
          {
            icon: Zap, iconColor: '#F59E0B', title: 'Qué hace automáticamente',
            body: [
              'Descarga tus <strong>movimientos bancarios</strong> cada día',
              'Detecta ingresos que coinciden con tus facturas (importe + IBAN + fecha)',
              'Marca la factura como <strong>"Pagada"</strong> sola',
              'Te avisa de movimientos no identificados para que los clasifiques',
            ],
          },
          {
            icon: Sparkles, iconColor: '#7C3AED', title: 'Bancos soportados',
            body: 'Todos los bancos españoles del SEPA: BBVA, Santander, CaixaBank, Sabadell, ING, Bankinter, Abanca, Kutxabank, Cajamar, Openbank, EVO, Wise, Revolut... La lista aparece al pulsar "Conectar banco".',
          },
          {
            icon: AlertTriangle, iconColor: '#EAB308', title: 'Si ves "MODO DEMO"',
            body: 'Significa que el proveedor PSD2 (GoCardless/Nordigen) aún no tiene credenciales reales configuradas en tu cuenta. Los datos que ves son simulados para probar la interfaz. Para activarlo en producción, contacta con soporte.',
          },
        ]}
      />
    </div>
  );
};

export default BankingPsd2Page;
